import React, { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { lookupTableService } from '../../services/lookupTableService';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { MagnifyingGlassIcon, PlusIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import DataGrid from '../../components/DataGrid';
import { ColumnDef } from '@tanstack/react-table';

const LookupTableModal: React.FC<{ item?: any | null; onClose: () => void; onSuccess?: () => void; }> = ({ item, onClose }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <h2 className="text-lg font-semibold mb-4">{item ? 'Edit Lookup Item' : 'Create Lookup Item'}</h2>
        <div className="text-sm text-gray-600 mb-4">Placeholder modal — implement fields and behaviors in components/Setup/LookupTableModal.tsx when available.</div>
        <div className="flex justify-end">
          <button onClick={onClose} className="px-3 py-1 bg-blue-600 text-white rounded">Close</button>
        </div>
      </div>
      <div className="fixed inset-0 bg-black opacity-30" onClick={onClose}></div>
    </div>
  );
};

const LookupTablePage: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<{ column: string; order: 'asc' | 'desc' }>({ column: 'category', order: 'asc' });
  const [category, setCategory] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [filters, setFilters] = useState<{ status?: 'ACTIVE' | 'INACTIVE' }>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selected, setSelected] = useState<any | null>(null);
  const pageSize = 50;
  const queryClient = useQueryClient();
  const { column: sortBy, order: sortOrder } = sort;

  const { data, isLoading, error } = useQuery({
    queryKey: ['lookup-tables', currentPage, searchTerm, sortBy, sortOrder, category, filters],
    queryFn: () => lookupTableService.getLookupTables({
      page: currentPage,
      limit: pageSize,
      search: searchTerm,
      sortBy,
      sortOrder,
      category: category || undefined,
      status: filters.status,
    })
  });

  const handleExport = useCallback(async () => {
    try {
      const blob = await lookupTableService.export({
        search: searchTerm,
        category: category || undefined,
        status: filters.status,
        ids: selectedIds.length ? selectedIds : undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.style.display = 'none'; a.href = url; a.download = `lookup-tables-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed', e); }
  }, [category, filters.status, searchTerm, selectedIds]);

  const rows = data?.lookupTables ?? [];
  const pagination = data?.pagination;

  const handleBulkAction = useCallback(async (action: 'activate' | 'deactivate') => {
    try {
      if (!selectedIds.length) return;
      const status = action === 'activate' ? 'ACTIVE' : 'INACTIVE';
      await lookupTableService.bulkUpdateStatus(selectedIds, status);
      setSelectedIds([]);
      queryClient.invalidateQueries({ queryKey: ['lookup-tables'] });
    } catch (e) {
      console.error('Bulk action failed', e);
    }
  }, [queryClient, selectedIds]);

  const toggleRow = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const handleToggleAll = useCallback((ids: string[], checked: boolean) => {
    setSelectedIds((prev) => (checked ? Array.from(new Set([...prev, ...ids])) : prev.filter((id) => !ids.includes(id))));
  }, []);

  const columns = useMemo<ColumnDef<any>[]>(() => [
    {
      id: 'category',
      accessorKey: 'category',
      header: 'Category',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'key',
      accessorKey: 'key',
      header: 'Key',
      meta: { sortable: true },
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'value',
      accessorKey: 'value',
      header: 'Value',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      meta: { sortable: true },
      cell: ({ row }) => (
        <span
          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
            row.original.status === 'ACTIVE'
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          }`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-end space-x-2">
          <button
            onClick={() => { setSelected(row.original); setIsModalOpen(true); }}
            className="text-primary-600 hover:text-primary-900"
          >
            <PencilSquareIcon className="w-4 h-4" />
          </button>
        </div>
      ),
    },
  ], []);

  const handleSortChange = (columnId: string) => {
    setSort((prev) => {
      if (prev.column === columnId) {
        return { column: columnId, order: prev.order === 'asc' ? 'desc' : 'asc' };
      }
      return { column: columnId, order: 'asc' };
    });
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">Error loading lookup table.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Lookup Table</h1>
        <div className="flex space-x-3">
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('activate')}>Activate</Button>
          <Button variant="outline" disabled={!selectedIds.length} onClick={() => handleBulkAction('deactivate')}>Deactivate</Button>
          <Button variant="secondary" onClick={handleExport}>Export</Button>
          <Button onClick={() => { setSelected(null); setIsModalOpen(true); }}><PlusIcon className="w-5 h-5 mr-2" />Create</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search lookup items..."
              className="pl-10 form-input"
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            />
          </div>

        </div>
      </div>

      <div className="bg-white rounded-lg shadow-soft">
        <div className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Category</label>
              <input
                type="text"
                className="form-input w-full"
                placeholder="Filter category..."
                value={category}
                onChange={(e) => {
                  setCategory(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="form-input w-full"
                value={filters.status ?? ''}
                onChange={(e) => {
                  setFilters((f) => ({ ...f, status: (e.target.value as 'ACTIVE' | 'INACTIVE' | '') || undefined }));
                  setCurrentPage(1);
                }}
              >
                <option value="">All</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12"><LoadingSpinner size="lg" /></div>
          ) : (
            <DataGrid
              data={rows}
              columns={columns}
              getRowId={(row) => row.id}
              enableSelection
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAll={handleToggleAll}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={handleSortChange}
            />
          )}
        </div>

        {pagination && (
          <div className="px-6 py-4 bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to <span className="font-medium">{Math.min(currentPage * pageSize, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
            </p>
            <div className="space-x-2">
              <Button variant="secondary" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}>Previous</Button>
              <Button variant="secondary" disabled={pagination && currentPage === pagination.pages} onClick={() => setCurrentPage((p) => (pagination ? Math.min(pagination.pages, p + 1) : p + 1))}>Next</Button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <LookupTableModal
          item={selected}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); queryClient.invalidateQueries({ queryKey: ['lookup-tables'] }); }}
        />
      )}
    </div>
  );
};

export default LookupTablePage;

